import { useEffect, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { newsList } from "@/mocks/news";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";

export default function NewsDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const article = useMemo(() => newsList.find((n) => n.id === id), [id]);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 泡泡影视`;
    }
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [article]);

  if (!article) {
    return (
      <div className="w-full px-4 md:px-8 pt-6">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影视资讯", to: "/news" }, { label: "内容不存在" }]} />
        <div className="mt-8">
          <EmptyState
            title="没有找到这篇资讯"
            description="它可能已被移除，或者链接有误。回到资讯列表看看其他内容吧。"
            actionLabel="返回资讯列表"
            onAction={() => navigate("/news")}
          />
        </div>
      </div>
    );
  }

  const related = newsList
    .filter((n) => n.id !== article.id && n.category === article.category)
    .slice(0, 4);
  const relatedFallback =
    related.length > 0 ? related : newsList.filter((n) => n.id !== article.id).slice(0, 4);

  return (
    <div className="w-full px-4 md:px-8 pt-6">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "影视资讯", to: "/news" },
          { label: article.title },
        ]}
      />

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <article className="lg:col-span-2">
          <div className="flex items-center gap-2 text-xs text-foreground-500">
            <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-secondary-900">
              {article.category}
            </span>
            <span>{article.date}</span>
          </div>
          <h1 className="mt-3 font-heading text-2xl md:text-3xl font-extrabold text-foreground-950 leading-snug">
            {article.title}
          </h1>
          <div className="mt-4 flex items-center gap-3 text-sm text-foreground-600">
            <span className="flex items-center gap-1.5">
              <span className="w-7 h-7 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center">
                <i className="ri-user-3-line" />
              </span>
              {article.author}
            </span>
          </div>

          <div className="mt-6 w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200">
            <img
              src={article.cover}
              alt={`${article.title} 配图`}
              title={`${article.title} 影视资讯`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="mt-6 space-y-4 text-sm md:text-base text-foreground-700 leading-8">
            {article.content.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {article.tags.map((tag) => (
              <span key={tag} className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600">
                #{tag}
              </span>
            ))}
          </div>

          <div className="mt-8 pt-6 border-t border-background-200">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm font-medium text-foreground-800 hover:border-primary-400 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line text-lg" />
              返回上一页
            </button>
          </div>
        </article>

        <aside className="lg:col-span-1">
          <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6 lg:sticky lg:top-24">
            <h2 className="font-heading text-base font-bold text-foreground-950">相关资讯</h2>
            <ul className="mt-4 space-y-3">
              {relatedFallback.map((item) => (
                <li key={item.id}>
                  <Link
                    to={`/news/detail/${item.id}`}
                    className="group block"
                  >
                    <span className="block text-sm font-medium text-foreground-900 group-hover:text-primary-700 transition-colors line-clamp-2">
                      {item.title}
                    </span>
                    <span className="mt-1 block text-xs text-foreground-500">{item.date}</span>
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/news"
              className="mt-5 flex items-center justify-center gap-1 px-4 py-2.5 rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看全部资讯
              <i className="ri-arrow-right-s-line text-lg" />
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}